/**
 * After zoom, re-center the scroll viewport. Horizontal position uses scroll-space math
 * (`maxL/2` + padding skew) so the scrollbar thumb stays centered as `scrollWidth` grows with zoom.
 * Vertical still prefers measuring the stage DOM vs the scroll viewport, with padding fallback.
 */

import { CANVAS_HORIZONTAL_VIEW_BIAS_PX } from "./mainCanvasLayout";

export function logCanvasHScroll(phase, data) {
}

function logCanvasScrollSnapshot(label, scrollArea, extra) {
  const sw = scrollArea.scrollWidth;
  const cw = scrollArea.clientWidth;
  const sh = scrollArea.scrollHeight;
  const ch = scrollArea.clientHeight;
  const hMax = Math.max(0, sw - cw);
  const vMax = Math.max(0, sh - ch);
  
}

/**
 * Snapshot horizontal scroll before a zoom change so we can preserve thumb ratio after layout updates.
 * @param {{ current: HTMLElement | null } | null} scrollAreaRef
 * @returns {{ left: number; maxL: number } | null}
 */
export function captureHorizontalScrollSnapshot(scrollAreaRef) {
  const el = scrollAreaRef?.current;
  if (!el) return null;
  const maxL = Math.max(0, el.scrollWidth - el.clientWidth);
  const snap = { left: el.scrollLeft, maxL };
  logCanvasHScroll("captureHorizontalScrollSnapshot", {
    scrollLeft: snap.left,
    maxL: snap.maxL,
    scrollWidth: el.scrollWidth,
    clientWidth: el.clientWidth,
  });
  return snap;
}

function clampScroll(scrollArea) {
  const maxL = Math.max(0, scrollArea.scrollWidth - scrollArea.clientWidth);
  const maxT = Math.max(0, scrollArea.scrollHeight - scrollArea.clientHeight);
  scrollArea.scrollLeft = Math.max(0, Math.min(maxL, scrollArea.scrollLeft));
  scrollArea.scrollTop = Math.max(0, Math.min(maxT, scrollArea.scrollTop));
}

/**
 * After a zoom factor change, adjust scroll so the document point under the viewport
 * position (clientX, clientY) stays under the pointer — used for Ctrl/Cmd + wheel zoom.
 * Works with CSS `transform: scale()` on descendants when scroll metrics track zoomed extent.
 */
export function applyScrollZoomToPoint(
  scrollArea,
  clientX,
  clientY,
  oldScale,
  newScale,
) {
  if (
    !scrollArea ||
    !(oldScale > 0) ||
    !(newScale > 0) ||
    oldScale === newScale
  ) {
    return;
  }
  const rect = scrollArea.getBoundingClientRect();
  let pl = 0;
  let pt = 0;
  try {
    const cs = getComputedStyle(scrollArea);
    pl = parseFloat(cs.paddingLeft) || 0;
    pt = parseFloat(cs.paddingTop) || 0;
  } catch {
    /* ignore */
  }
  const vx = clientX - rect.left - scrollArea.clientLeft - pl;
  const vy = clientY - rect.top - scrollArea.clientTop - pt;
  const factor = newScale / oldScale;
  const maxL = Math.max(0, scrollArea.scrollWidth - scrollArea.clientWidth);
  const maxT = Math.max(0, scrollArea.scrollHeight - scrollArea.clientHeight);
  const nextL = (scrollArea.scrollLeft + vx) * factor - vx;
  const nextT = (scrollArea.scrollTop + vy) * factor - vy;
  scrollArea.scrollLeft = Math.max(0, Math.min(maxL, nextL));
  scrollArea.scrollTop = Math.max(0, Math.min(maxT, nextT));
}

/**
 * Center of the scrollport (the visible region between padding/border), in viewport coordinates.
 * Must match how `.canvas-scroll-area` is styled: large asymmetric padding for off-canvas + zoom overflow.
 */
/** Matches MyCanvas fixed overlay scrollbar tracks (v: right 12px, h: bottom 12px). */
const OVERLAY_SCROLLBAR_THICKNESS_PX = 12;

function getScrollAreaViewportCenterInViewport(scrollArea) {
  const rect = scrollArea.getBoundingClientRect();
  let cs;
  try {
    cs = getComputedStyle(scrollArea);
  } catch {
    const half = OVERLAY_SCROLLBAR_THICKNESS_PX / 2;
    return {
      cx: rect.left + scrollArea.clientWidth / 2,
      cy: rect.top + scrollArea.clientHeight / 2 - half,
    };
  }
  const borderL = parseFloat(cs.borderLeftWidth) || 0;
  const borderT = parseFloat(cs.borderTopWidth) || 0;
  const pl = parseFloat(cs.paddingLeft) || 0;
  const pr = parseFloat(cs.paddingRight) || 0;
  const pt = parseFloat(cs.paddingTop) || 0;
  const pb = parseFloat(cs.paddingBottom) || 0;
  const innerW = Math.max(0, scrollArea.clientWidth - pl - pr);
  const innerH = Math.max(0, scrollArea.clientHeight - pt - pb);
  const halfBar = OVERLAY_SCROLLBAR_THICKNESS_PX / 2;
  // Horizontal: use geometric center of the scrollport. A fixed −6px tweak for the vertical overlay
  // skewed high‑zoom centering once `transform: scale()` grew the main stage (see prior drift >150%).
  // Vertical: nudge up slightly so content isn’t centered under the bottom overlay track.
  return {
    cx: rect.left + borderL + pl + innerW / 2,
    cy: rect.top + borderT + pt + innerH / 2 - halfBar,
  };
}

/**
 * Prefer the DOM block that matches the visible main canvas (`#main-canvas-stage-container`);
 * Konva's `stage.container()` can disagree with that box (canvas vs wrapper), which skews centering.
 */
function resolveCenterTargetElement(scrollArea, stageRef, mainStageContainerRef) {
  const fromParent = mainStageContainerRef?.current;
  if (
    fromParent &&
    typeof fromParent.getBoundingClientRect === "function" &&
    scrollArea.contains(fromParent)
  ) {
    return fromParent;
  }
  if (typeof document !== "undefined") {
    const byId = document.getElementById("main-canvas-stage-container");
    if (
      byId &&
      typeof byId.getBoundingClientRect === "function" &&
      scrollArea.contains(byId)
    ) {
      return byId;
    }
  }
  try {
    const stage = stageRef?.current;
    const c =
      stage && typeof stage.container === "function" ? stage.container() : null;
    if (
      c &&
      typeof c.getBoundingClientRect === "function" &&
      scrollArea.contains(c)
    ) {
      return c;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Horizontal scroll position that centers the scrollable range (thumb) and matches symmetric layout.
 * We use **scroll-space** math (`maxL/2` + padding skew), not `getBoundingClientRect` deltas: when
 * zoom grows `scrollWidth` (spacer), screen-space stage vs viewport can stay ~aligned while
 * `scrollLeft` lags, so `scrollLeft/maxL` drifts left (see logs: delta≈0, thumb ratio ≈0.38).
 */
export function computeHorizontallyCenteredScrollLeft(
  scrollArea,
  _stageRef,
  _mainStageContainerRef,
  biasLeftPx = CANVAS_HORIZONTAL_VIEW_BIAS_PX,
) {
  if (!scrollArea) return 0;
  const maxL = Math.max(0, scrollArea.scrollWidth - scrollArea.clientWidth);

  const applyBias = (raw) =>
    Math.max(0, Math.min(maxL, raw - (Number(biasLeftPx) || 0)));

  let padL = 0;
  let padR = 0;
  try {
    const cs = getComputedStyle(scrollArea);
    padL = parseFloat(cs.paddingLeft) || 0;
    padR = parseFloat(cs.paddingRight) || 0;
  } catch {
    /* use zeros */
  }
  const raw = Math.round(maxL / 2 + (padR - padL) / 2);
  const result = applyBias(raw);
  logCanvasHScroll("computeHorizontallyCenteredScrollLeft:scrollSpace", {
    maxL,
    scrollWidth: scrollArea.scrollWidth,
    clientWidth: scrollArea.clientWidth,
    padL,
    padR,
    raw,
    biasLeftPx,
    result,
    thumbRatio: maxL > 0 ? result / maxL : 0,
  });
  return result;
}

/**
 * Vertical scroll position that centers the main Konva stage in the scroll viewport (mirrors horizontal logic).
 */
export function computeVerticallyCenteredScrollTop(
  scrollArea,
  stageRef,
  mainStageContainerRef,
) {
  if (!scrollArea) return 0;
  const maxT = Math.max(0, scrollArea.scrollHeight - scrollArea.clientHeight);

  try {
    const targetEl = resolveCenterTargetElement(
      scrollArea,
      stageRef,
      mainStageContainerRef,
    );
    if (targetEl) {
      const cr = targetEl.getBoundingClientRect();
      const { cy: viewportCy } = getScrollAreaViewportCenterInViewport(scrollArea);
      const stageCy = cr.top + cr.height / 2;
      const delta = stageCy - viewportCy;
      const next = Math.round(scrollArea.scrollTop + delta);
      return Math.max(0, Math.min(maxT, next));
    }
  } catch {
    /* fall through */
  }

  let padT = 0;
  let padB = 0;
  try {
    const cs = getComputedStyle(scrollArea);
    padT = parseFloat(cs.paddingTop) || 0;
    padB = parseFloat(cs.paddingBottom) || 0;
  } catch {
    /* use zeros */
  }
  return Math.round(maxT / 2 + (padB - padT) / 2);
}

/**
 * Re-applies horizontal + vertical centering until scroll position stabilizes or `maxPasses`
 * is reached. Fixes drift when `scrollWidth` / `maxL` update between frames (common above ~150%
 * zoom after padding and spacer catch up to scale).
 */
export function settleScrollViewportOnMainStage(
  scrollArea,
  stageRef,
  mainStageContainerRef,
  maxPasses = 10,
) {
  if (!scrollArea) return;
  let stoppedAtPass = maxPasses - 1;
  for (let pass = 0; pass < maxPasses; pass++) {
    const beforeL = scrollArea.scrollLeft;
    const beforeT = scrollArea.scrollTop;
    scrollArea.scrollLeft = computeHorizontallyCenteredScrollLeft(
      scrollArea,
      stageRef,
      mainStageContainerRef,
    );
    scrollArea.scrollTop = computeVerticallyCenteredScrollTop(
      scrollArea,
      stageRef,
      mainStageContainerRef,
    );
    void scrollArea.scrollWidth;
    void scrollArea.scrollHeight;
    const settled =
      Math.abs(scrollArea.scrollLeft - beforeL) < 0.75 &&
      Math.abs(scrollArea.scrollTop - beforeT) < 0.75;
    stoppedAtPass = pass;
    if (settled) {
      break;
    }
  }
  clampScroll(scrollArea);
  logCanvasScrollSnapshot("settleScrollViewportOnMainStage", scrollArea, {
    passesUsed: stoppedAtPass + 1,
  });
}

/**
 * Center the scroll viewport on the main stage (horizontal + vertical). Used after reset zoom and zoom-out.
 */
export function centerScrollViewportOnScaledCanvas(
  scrollArea,
  _scaledEl,
  stageRef,
  mainStageContainerRef,
) {
  if (!scrollArea) return;
  try {
    const beforeL = scrollArea.scrollLeft;
    const beforeT = scrollArea.scrollTop;
    const nextL = computeHorizontallyCenteredScrollLeft(
      scrollArea,
      stageRef,
      mainStageContainerRef,
    );
    const nextT = computeVerticallyCenteredScrollTop(
      scrollArea,
      stageRef,
      mainStageContainerRef,
    );
    scrollArea.scrollLeft = nextL;
    scrollArea.scrollTop = nextT;
    logCanvasHScroll("centerScrollViewportOnScaledCanvas", {
      beforeLeft: beforeL,
      afterLeft: nextL,
      beforeTop: beforeT,
      afterTop: nextT,
      maxL: Math.max(0, scrollArea.scrollWidth - scrollArea.clientWidth),
      maxT: Math.max(0, scrollArea.scrollHeight - scrollArea.clientHeight),
    });
    clampScroll(scrollArea);
  } catch {
    const maxLeft = Math.max(
      0,
      scrollArea.scrollWidth - scrollArea.clientWidth,
    );
    const maxTop = Math.max(
      0,
      scrollArea.scrollHeight - scrollArea.clientHeight,
    );
    let left = maxLeft / 2;
    let top = maxTop / 2;
    try {
      left = computeHorizontallyCenteredScrollLeft(
        scrollArea,
        stageRef,
        mainStageContainerRef,
      );
      top = computeVerticallyCenteredScrollTop(
        scrollArea,
        stageRef,
        mainStageContainerRef,
      );
    } catch {
      /* keep halves */
    }
    scrollArea.scrollTo({
      left,
      top,
      behavior: "auto",
    });
  }
}

function runWhenScrollMetricsReady(
  scrollAreaRef,
  scaledContentRef,
  stageRef,
  mainStageContainerRef,
) {
  let runCount = 0;
  const run = (label) => {
    runCount += 1;
    logCanvasHScroll("runWhenScrollMetricsReady", { label, runCount });
    centerScrollViewportOnScaledCanvas(
      scrollAreaRef?.current,
      scaledContentRef?.current,
      stageRef,
      mainStageContainerRef,
    );
  };

  // One frame after layout; one retry after padding/ResizeObserver (zoom overflow).
  requestAnimationFrame(() => {
    run("raf1");
    requestAnimationFrame(() => run("raf2"));
  });
  window.setTimeout(() => run("t0"), 0);
  window.setTimeout(() => run("t48"), 48);
  // Zoom overflow padding / spacer width can update after the first paint; re-measure once more.
  window.setTimeout(() => run("t120"), 120);
}

/**
 * Run after React applies the new zoom so scrollWidth reflects padded/zoomed content.
 */
export function scheduleCenterScrollOnScaledCanvas(
  scrollAreaRef,
  scaledContentRef,
  stageRef,
  mainStageContainerRef,
) {
  logCanvasHScroll("scheduleCenterScrollOnScaledCanvas", {
    reason: "queued (e.g. reset zoom)",
  });
  setTimeout(() => {
    runWhenScrollMetricsReady(
      scrollAreaRef,
      scaledContentRef,
      stageRef,
      mainStageContainerRef,
    );
  }, 0);
}
